import clsx from "clsx";
import { View, Text, Tooltip, Icon } from "./base";

type LatencyData = {
  stt_first_transcription_frame?: number;
  handler_first_speech_event?: number;
  tts_first_audio_frame?: number;
  turn_taking?: number;
  function_runtime?: number;
};

type TurnLatencyTooltipProps = {
  latencies?: LatencyData;
  duration?: number;
  turnNumber?: number;
  children?: React.ReactNode;
};

const LatencyRow = ({ label, value, textClass, dotClass }: { label: string; value?: number; textClass: string; dotClass: string }) => (
  <View className="flex items-center gap-2 py-0.5">
    <View className={clsx("w-2 h-2 rounded-full", dotClass)} />
    <Text className={clsx("text-xs", textClass)}>
      {label}: {value !== undefined ? `${value} ms` : "—"}
    </Text>
  </View>
);

export const TurnLatencyTooltip = ({ latencies, duration, turnNumber, children }: TurnLatencyTooltipProps) => {
  // Check if we have any latency data
  const hasLatencyData =
    latencies &&
    (latencies.stt_first_transcription_frame !== undefined ||
      latencies.handler_first_speech_event !== undefined ||
      latencies.tts_first_audio_frame !== undefined ||
      latencies.turn_taking !== undefined);

  // Hide tooltip completely for old sessions without useful data
  // Only show if we have either:
  // 1. Turn number AND (latency data OR duration), OR
  // 2. Both latency data AND duration (for edge cases)
  const shouldShowTooltip = (turnNumber !== undefined && (hasLatencyData || duration)) || (hasLatencyData && duration);

  if (!shouldShowTooltip) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      content={
        <View className="p-2">
          <Text className="text-xs font-medium mb-2 text-gray-700 dark:text-gray-300">Turn {turnNumber} Latencies</Text>
          <View className="space-y-1">
            <LatencyRow
              label="Time to First Transcription"
              textClass="text-purple-500"
              dotClass="bg-purple-500"
              value={latencies?.stt_first_transcription_frame}
            />
            <LatencyRow
              label="Time to First Speech Event"
              textClass="text-blue-600"
              dotClass="bg-blue-600"
              value={latencies?.handler_first_speech_event}
            />
            <LatencyRow
              label="Time to First Audio"
              textClass="text-yellow-600"
              dotClass="bg-yellow-600"
              value={latencies?.tts_first_audio_frame}
            />
            <LatencyRow
              label="End-to-End Turn Taking"
              textClass="text-green-600"
              dotClass="bg-green-600"
              value={latencies?.turn_taking}
            />
            <LatencyRow
              label="Function Runtime"
              textClass="text-cyan-400"
              dotClass="bg-cyan-400"
              value={duration ? Math.round(duration) : latencies?.function_runtime}
            />
          </View>
        </View>
      }
      placement="top"
    >
      {children || (
        <View className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 select-none cursor-default hover:text-gray-700 dark:hover:text-gray-300">
          <Icon
            name="clock"
            size={12}
            className="text-gray-500 dark:text-gray-400"
          />
          <Text className="text-xs">{latencies?.turn_taking ? `${Math.round(latencies.turn_taking)}ms` : "Latencies"}</Text>
        </View>
      )}
    </Tooltip>
  );
};
